import React, { useEffect, useState } from "react";
import { Plus, Pencil, KeyRound, ShieldCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ROLE_LABELS, roleLabelsOf, useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import SearchableSelect from "../components/SearchableSelect";
import { toast } from "sonner";

const EMPTY = { nama: "", username: "", email: "", password: "", role: "", roles: [], status: "Aktif", komisi: "", kelas_sm: "" };
const KELAS_SM_OPTS = ["Kecil", "Besar", "Menengah", "Pra Remaja"];
const KOMISI_OPTS = ["IBR Minggu", "Komisi Anak", "Komisi Remaja/Pemuda", "Komisi Wanita", "Komisi Pria", "Komisi Umas"];
const ROLE_OPTS = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

export default function UsersPage() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditId(u.id); setForm({ nama: u.nama, username: u.username, email: u.email, password: "", role: u.role, roles: u.roles?.length ? u.roles : [u.role], status: u.status, komisi: u.komisi || "", kelas_sm: u.kelas_sm || "" }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRole = (r) => setForm((f) => { const roles = f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r]; return { ...f, roles, role: roles[0] || "" }; });
  const punya = (r) => form.roles.includes(r);

  const save = async () => {
    if (!form.roles.length) return toast.error("Pilih minimal satu role");
    setSaving(true);
    try {
      if (editId) {
        const { password, ...rest } = form;
        await api.put(`/users/${editId}`, rest);
        toast.success("User diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("User baru dibuat");
      }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { status: u.status === "Aktif" ? "Nonaktif" : "Aktif" });
      toast.success(`User ${u.status === "Aktif" ? "dinonaktifkan" : "diaktifkan"}`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doReset = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { password_baru: newPw });
      toast.success("Password direset"); setResetTarget(null); setNewPw("");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Manajemen User</h1>
          <p className="text-sm text-slate-500 mt-1">Total <span className="font-semibold" data-testid="users-total">{items.length}</span> user</p>
        </div>
        <Button onClick={openCreate} className="min-h-[44px] rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" /> Tambah User</Button>
      </div>

      <div className="space-y-3" data-testid="users-list">
        {items.map((u) => (
          <Card key={u.id} className="p-4 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`user-row-${u.username}`}>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 flex items-center gap-2">{u.nama} {u.id === me.id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Anda</span>}</div>
              <div className="text-xs text-slate-500 font-mono truncate">{u.username} · {u.email}</div>
              <div className="flex gap-2 mt-2">
                <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold flex items-center gap-1 flex-wrap" data-testid={`user-roles-${u.username}`}><ShieldCheck className="h-3 w-3" />{roleLabelsOf(u)}{u.komisi ? ` · ${u.komisi}` : ""}{u.kelas_sm ? ` · Kelas ${u.kelas_sm}` : ""}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${u.status === "Aktif" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`user-status-${u.username}`}>{u.status}</span>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => { setResetTarget(u); setNewPw(""); }} data-testid={`reset-pw-${u.username}`}><KeyRound className="h-3.5 w-3.5 mr-1.5" />Reset Password</Button>
              {u.id !== me.id && <Button variant="outline" size="sm" className="rounded-xl" onClick={() => toggleStatus(u)} data-testid={`toggle-status-${u.username}`}>{u.status === "Aktif" ? "Nonaktifkan" : "Aktifkan"}</Button>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editId ? "Edit User" : "Tambah User"}</DialogTitle><DialogDescription>Hanya Admin yang dapat mengelola user dan role.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <F label="Nama"><Input data-testid="user-nama" value={form.nama} onChange={(e) => set("nama", e.target.value)} className="min-h-[44px] rounded-xl" /></F>
            <F label="Username"><Input data-testid="user-username" value={form.username} onChange={(e) => set("username", e.target.value)} className="min-h-[44px] rounded-xl font-mono" /></F>
            <F label="Email"><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="min-h-[44px] rounded-xl" /></F>
            {!editId && <F label="Password (min. 8 karakter)"><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="min-h-[44px] rounded-xl" /></F>}
            <F label={`Role (bisa lebih dari satu · ${form.roles.length} dipilih)`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto rounded-xl border p-2" data-testid="user-roles-picker">
                {ROLE_OPTS.map((o) => <label key={o.value} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${punya(o.value) ? "bg-indigo-50 text-indigo-800 font-medium" : "hover:bg-slate-50"}`}><input type="checkbox" checked={punya(o.value)} onChange={() => toggleRole(o.value)} data-testid={`user-role-${o.value}`} />{o.label}</label>)}
              </div>
              <p className="text-xs text-slate-500 mt-1">Akses akun = gabungan semua role yang dipilih. Role utama: <b>{ROLE_LABELS[form.role] || "-"}</b>.</p>
            </F>
            {punya("GURU_SM") && <F label="Kelas Sekolah Minggu yang diampu"><SearchableSelect options={KELAS_SM_OPTS} value={form.kelas_sm} onChange={(v) => set("kelas_sm", v || "")} placeholder="Pilih kelas" testId="user-kelas-sm-select" /><p className="text-xs text-slate-500 mt-1">Guru SM hanya melihat Data Anak di kelas ini.</p></F>}
            {punya("KETUA_KOMISI") && <F label="Komisi yang dipimpin"><SearchableSelect options={KOMISI_OPTS} value={form.komisi} onChange={(v) => set("komisi", v || "")} placeholder="Pilih komisi" testId="user-komisi-select" /><p className="text-xs text-slate-500 mt-1">Ketua Komisi hanya melihat Kartu Keluarga & Satu Atap anggota komisi ini.</p></F>}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="rounded-xl bg-[hsl(224,64%,33%)]" onClick={save} disabled={saving} data-testid="save-user-btn">{saving ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(resetTarget)} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>Password baru untuk {resetTarget?.nama}. User akan diminta mengganti password saat login.</DialogDescription></DialogHeader>
          <F label="Password baru (min. 8 karakter)"><Input data-testid="reset-pw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="min-h-[44px] rounded-xl" /></F>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setResetTarget(null)}>Batal</Button>
            <Button className="rounded-xl bg-[hsl(224,64%,33%)]" onClick={doReset} disabled={saving || newPw.length < 8} data-testid="confirm-reset-pw-btn">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (<div><Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>{children}</div>);
